import type { BlogPost } from '../../../../app/features/blog/blog.types';

type RawPost = Omit<BlogPost, 'readingTimeMinutes'>;

export const angularRxjsPosts: RawPost[] = [
  {
    slug: 'signals-vs-observables',
    title: 'Signals vs Observables: dos enfoques, un mismo problema',
    description:
      'Cuándo usar Angular Signals y cuándo confiar en RxJS. Dos implementaciones del mismo contador para ilustrar la diferencia conceptual.',
    date: '2026-04-14',
    content: `## El problema

Angular 17 introdujo Signals como primitiva reactiva de primera clase. Desde entonces, la pregunta recurrente en equipos es la misma: ¿cuándo usar un Signal y cuándo un Observable?

La confusión es legítima. Ambos resuelven el mismo problema en superficie: propagar cambios de estado a la interfaz. Pero el modelo mental que requieren es distinto.

## La solución

Un **Signal** es un valor sincrónico con seguimiento de dependencias. Angular sabe qué componentes leen ese valor y los marca para re-renderizado únicamente cuando cambia. No hay suscripciones, no hay gestión de ciclo de vida.

\`\`\`typescript
// Signal
count = signal(0);
increment() { this.count.update(v => v + 1); }
\`\`\`

Un **Observable** es una secuencia de valores en el tiempo, potencialmente asíncrona, que requiere suscripción explícita. Su potencia está en la composición: \`merge\`, \`switchMap\`, \`debounceTime\`.

\`\`\`typescript
// Observable
count$ = new BehaviorSubject(0);
increment() { this.count$.next(this.count$.value + 1); }
\`\`\`

El playground muestra las dos implementaciones del mismo contador:

<iframe src="https://stackblitz.com/edit/angular-signals-vs-observables?embed=1&file=src/app/app.component.ts" width="100%" height="500" title="Signals vs Observables"></iframe>

## Puntos clave

- Signals son sincrónicas y sin suscripción: ideales para estado local de componente
- Observables modelan flujos en el tiempo: eventos DOM, WebSockets, HTTP con reintentos
- \`toSignal()\` y \`toObservable()\` permiten interoperar sin reescribir lógica existente

## Cuándo aplicar este patrón

Usar Signals para estado de UI simple: formularios, contadores, toggles. Usar Observables cuando el tiempo importa: datos en tiempo real, operaciones con cancelación, streams compuestos. En la práctica, un mismo servicio puede exponer ambos según el contexto del consumidor.
`,
  },
  {
    slug: 'facade-pattern-angular',
    title: 'Facade Pattern en Angular: componentes tontos, lógica centralizada',
    description:
      'Cómo separar responsabilidades con la arquitectura Componente → Facade → Servicio. Menos lógica en templates, más testabilidad.',
    date: '2026-04-14',
    content: `## El problema

Un componente que inyecta tres servicios, gestiona cinco subscripciones y toma decisiones de negocio en el template es, técnicamente, un componente que hace demasiado. La consecuencia directa es que resulta difícil de testear, difícil de reutilizar y costoso de modificar.

El patrón Facade resuelve esto interponiendo una capa entre el componente y la infraestructura de datos.

## La solución

La arquitectura es lineal: \`Componente → Facade → Servicio\`. El componente solo conoce la Facade. La Facade orquesta servicios, transformaciones de estado y lógica de negocio. Los servicios solo se comunican con el backend.

\`\`\`typescript
@Injectable({ providedIn: 'root' })
export class ProductFacade {
  private readonly store = inject(ProductStore);
  private readonly api = inject(ProductApiService);

  readonly products = this.store.products;
  readonly isLoading = this.store.isLoading;

  public loadProducts(): void {
    this.api.getAll().subscribe(products => this.store.setProducts(products));
  }

  public addToCart(id: string): void {
    this.store.addToCart(id);
  }
}
\`\`\`

El componente queda reducido a presentación pura:

\`\`\`typescript
@Component({ ... })
export class ProductListComponent {
  protected readonly facade = inject(ProductFacade);

  public ngOnInit() { this.facade.loadProducts(); }
}
\`\`\`

<iframe src="https://stackblitz.com/edit/angular-facade-pattern?embed=1&file=src/app/products/product.facade.ts" width="100%" height="500" title="Facade Pattern"></iframe>

## Puntos clave

- El componente no conoce los servicios de infraestructura: solo la Facade
- Testear el componente requiere mockear una sola dependencia
- La Facade puede cambiar la implementación interna sin afectar el template

## Cuándo aplicar este patrón

Cuando un componente empieza a inyectar más de dos servicios o cuando la lógica de negocio aparece en el template. En aplicaciones de escala, la Facade también actúa como punto de entrada para telemetría, logging y caché sin contaminar los componentes.
`,
  },
  {
    slug: 'rxjs-operators-trading',
    title: 'RxJS en tiempo real: operadores para datos de alta frecuencia',
    description:
      'bufferTime, switchMap y distinctUntilChanged aplicados a un dashboard de trading que recibe miles de actualizaciones por segundo.',
    date: '2026-04-14',
    content: `## El problema

Un WebSocket de datos de mercado puede emitir entre 500 y 5000 actualizaciones por segundo. Renderizar cada una de ellas en el DOM es imposible: el hilo principal se satura antes de que el usuario perciba ningún cambio. El problema no es la cantidad de datos, sino la frecuencia de actualizaciones del estado.

## La solución

Tres operadores combinados resuelven el problema de forma declarativa:

**\`bufferTime\`** acumula emisiones durante una ventana temporal y las entrega como array. En lugar de 500 renders por segundo, el componente recibe un batch cada 100ms y aplica solo el último valor por instrumento.

\`\`\`typescript
this.ws.prices$.pipe(bufferTime(100),filter(batch => batch.length > 0),
  map(batch => this.mergeLatestBySymbol(batch)),
).subscribe(prices => this.store.updatePrices(prices));
\`\`\`

**\`distinctUntilChanged\`** evita re-renders innecesarios cuando el valor no ha cambiado. Crítico para columnas de P&L donde el precio subyacente fluctúa pero el resultado neto permanece igual.

**\`switchMap\`** cancela la solicitud anterior al cambiar de instrumento activo. Sin él, una respuesta lenta de un instrumento anterior podría sobreescribir los datos actuales.

\`\`\`typescript
selectedSymbol$.pipe(
  switchMap(symbol => this.api.getOrderBook(symbol)),distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
).subscribe(book => this.orderBook.set(book));
\`\`\`

<iframe src="https://stackblitz.com/edit/rxjs-trading-operators?embed=1&file=src/app/trading.service.ts" width="100%" height="500" title="RxJS Trading Operators"></iframe>

## Puntos clave

- \`bufferTime\` reduce la frecuencia de actualización sin perder el último valor
- \`switchMap\` garantiza que solo la última solicitud activa tiene efecto
- \`distinctUntilChanged\` elimina re-renders cuando el estado no cambia semánticamente

## Cuándo aplicar este patrón

En cualquier interfaz que consuma streams de alta frecuencia: dashboards de monitorización, feeds de sensores IoT, colaboración en tiempo real. La combinación \`bufferTime + mergeLatest\` es especialmente útil cuando múltiples fuentes emiten sobre el mismo identificador.
`,
  },
  {
    slug: 'repository-pattern-angular',
    title: 'Repository Pattern en Angular: por qué tus servicios no deberían conocer HTTP',
    description:
      'Cómo aplicar Dependency Inversion en servicios Angular con InjectionToken + Interface + Implementation. Tests más simples, código más mantenible.',
    date: '2026-04-15',
    content: `## El problema que no parece problema

Abrís cualquier proyecto Angular y encontrás esto:

\`\`\`typescript
@Injectable({ providedIn: 'root' })
export class PortfolioService {
  private readonly http = inject(HttpClient);

  public getProjects(): Observable<Project[]> {
    return this.http.get<Project[]>('/api/projects');
  }
}
\`\`\`

Funciona. TypeScript está contento. El linter no se queja. El problema es invisible hasta que intentás escribir un test unitario para un componente que inyecta este servicio:

\`\`\`typescript
// En el test...
TestBed.configureTestingModule({
  providers: [
    {
      provide: PortfolioService,
      useValue: { getProjects: () => of(mockProjects) },
    },
  ],
});
\`\`\`

Esto funciona, pero es frágil: si \`PortfolioService\` agrega un método nuevo, el mock silenciosamente no lo tiene. Y si algún día cambiás el transporte —de HTTP a GraphQL, a un SDK de Supabase, a datos locales para un Storybook— tenés que tocar el servicio y todos sus tests.

El servicio está acoplado a \`HttpClient\`. Inversión de dependencias dice que debería estar acoplado a una abstracción.

## La solución: InjectionToken + Interface + Implementation

El patrón tiene tres piezas:

**1. La interfaz** — el contrato que el resto de la app conoce:

\`\`\`typescript
// portfolio.repository.ts
export interface PortfolioRepository {
  public getProjects(): Observable<Project[]>;
  public getProject(slug: string): Observable<Project | null>;
  public getSkills(): Observable<Skill[]>;
}

export const PORTFOLIO_REPOSITORY =
  new InjectionToken<PortfolioRepository>('PortfolioRepository');
\`\`\`

**2. La implementación HTTP** — la única clase que conoce \`HttpClient\`:

\`\`\`typescript
// http-portfolio.repository.ts
@Injectable()
export class HttpPortfolioRepository implements PortfolioRepository {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  public getProjects(): Observable<Project[]> {
    return this.http.get<Project[]>(\`\${this.baseUrl}/projects\`);
  }

  public getProject(slug: string): Observable<Project | null> {
    return this.http.get<Project>(\`\${this.baseUrl}/projects/\${slug}\`).pipe(
      catchError(() => of(null))
    );
  }

  public getSkills(): Observable<Skill[]> {
    return this.http.get<Skill[]>(\`\${this.baseUrl}/skills\`);
  }
}
\`\`\`

**3. El registro en DI** — en \`app.config.ts\`:

\`\`\`typescript
export const appConfig: ApplicationConfig = {
  providers: [ provideHttpClient(withFetch()),
    {
      provide: PORTFOLIO_REPOSITORY,
      useClass: HttpPortfolioRepository,
    },
    // ...resto de providers
  ],
};
\`\`\`

Los componentes y facades inyectan el token, no la clase concreta:

\`\`\`typescript
@Injectable({ providedIn: 'root' })
export class PortfolioFacade {
  private readonly repo = inject(PORTFOLIO_REPOSITORY);

  readonly projects$ = this.repo.getProjects();
}
\`\`\`

## Cómo simplifica los tests

Con este patrón, el mock del repositorio es un objeto que implementa la interfaz. TypeScript te avisa si le falta algún método:

\`\`\`typescript
// mock-portfolio.repository.ts (reutilizable en todos los tests)
export class MockPortfolioRepository implements PortfolioRepository {
  private projects = signal<Project[]>([]);

  public setProjects(projects: Project[]): void {
    this.projects.set(projects);
  }

  public getProjects(): Observable<Project[]> {
    return toObservable(this.projects);
  }

  public getProject(slug: string): Observable<Project | null> {
    return of(this.projects().find(p => p.slug === slug) ?? null);
  }

  public getSkills(): Observable<Skill[]> {
    return of([]);
  }
}

// En el test
const mockRepo = new MockPortfolioRepository();
mockRepo.setProjects([{ slug: 'test', title: 'Test Project', ... }]);

TestBed.configureTestingModule({
  providers: [
    { provide: PORTFOLIO_REPOSITORY, useValue: mockRepo },
  ],
});
\`\`\`

No hay \`HttpClientTestingModule\`. No hay intercepción de peticiones HTTP. El test describe el comportamiento del componente, no los detalles de transporte.

## Cuándo es overkill y cuándo te salva

**Es overkill cuando:**
- El servicio tiene un único método y un único consumidor
- El proyecto es un prototipo que no va a tener tests
- La fuente de datos nunca va a cambiar (datos estáticos en JSON)

**Te salva cuando:**
- Tenés que testear lógica de negocio que depende de datos remotos
- Usás Storybook o necesitás una implementación con datos ficticios
- El backend puede cambiar (migración de REST a GraphQL, de NestJS a Supabase direct)
- Varios equipos trabajan en paralelo y el backend no está listo

En este portfolio, el patrón permitió desarrollar toda la UI contra una implementación mock antes de que el backend de NestJS estuviera desplegado. Cuando el backend estuvo listo, cambié un provider en \`app.config.ts\` y todo funcionó sin tocar un solo componente.

## El código real de este portfolio

\`\`\`typescript
// src/app/core/repositories/portfolio.repository.ts
export const PORTFOLIO_REPOSITORY =
  new InjectionToken<PortfolioRepository>('PortfolioRepository');

// src/app/core/repositories/http-portfolio.repository.ts
@Injectable()
export class HttpPortfolioRepository implements PortfolioRepository {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);

  public getProjects(): Observable<Project[]> {
    return this.http
      .get<ApiResponse<Project[]>>(\`\${this.env.apiUrl}/projects\`)
      .pipe(map(r => r.data));
  }
}
\`\`\`

La abstracción no añade complejidad accidental. Añade una seam: un punto de corte donde podés cambiar la implementación sin afectar al resto del sistema. Eso es exactamente lo que Dependency Inversion promete.
`,
  },
  {
    slug: 'angular-21-signals-rxresource',
    title: 'De lazySignal() a rxResource(): migrando estado asíncrono en Angular 21',
    description:
      'Migración real de un helper personalizado con EnvironmentInjector a la API oficial rxResource() de Angular 21. Código antes/después y el gotcha de stream vs loader.',
    date: '2026-04-15',
    content: `## El problema original: estado asíncrono con Signals

Angular Signals resuelve el estado sincrónico de forma elegante. El problema llegaba con datos asíncronos: si querías cargar datos de una API y exponerlos como Signal, no había una API oficial. Terminabas escribiendo un helper:

\`\`\`typescript
// lazySignal.ts — helper personalizado (pre-Angular 21)
export function lazySignal<T>(
  loader: () => Observable<T>,
  injector: EnvironmentInjector
): Signal<T | undefined> {
  const result = signal<T | undefined>(undefined);

  public runInInjectionContext(injector, () => {
    public loader().pipe(takeUntilDestroyed()).subscribe(value => {
      result.set(value);
    });
  });

  return result.asReadonly();
}
\`\`\`

Angular 19/21 introdujo \`rxResource()\` y \`resource()\` para resolver esto de forma nativa.

## La solución: rxResource()

La migración simplifica el código y delega la gestión del ciclo de vida a Angular:

\`\`\`typescript
// Angular 21
readonly products = rxResource({
  loader: () => this.api.getAll()
});
\`\`\`

Ahora tenemos acceso a \`products.value()\`, \`products.isLoading()\` y \`products.error()\` de forma automática.

## El Gotcha: stream vs loader

Un error común es intentar pasar un Observable directamente al \`rxResource()\`. La API espera una función \`loader\` que devuelva el Observable, o un objeto con \`request\` si el cargador depende de otros triggers.

\`\`\`typescript
// Correcto para peticiones dependientes
readonly user = rxResource({
  request: () => this.userId(),
  loader: ({ request: id }) => this.api.getUser(id)
});
\`\`\`

## Cuándo aplicar este patrón

Siempre que necesites exponer datos asíncronos como Signals. Elimina la necesidad de suscripciones manuales y maneja los estados de carga por ti.
`,
  },
];
